import React, { createContext, useState, useContext, useEffect } from 'react';
import * as firebaseService from '../services/firebaseService';
import { saveLastSelectedShop, getLastSelectedShop } from '../services/shopService';
import { useAuth } from './AuthContext';
import apiService from '../services/apiService';

const ShopContext = createContext();

export function useShop() {
  return useContext(ShopContext);
}

export function ShopProvider({ children }) {
  const [shops, setShops] = useState([]);
  const [currentShop, setCurrentShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load saved shop data when user logs in
  useEffect(() => {
    if (user) {
      loadShops();
    } else {
      setShops([]);
      setCurrentShop(null);
      setLoading(false);
    }
  }, [user]);

  // Expose currentShop on window so services (like shopifyBulkOperationsService) can
  // always access the latest token, avoiding stale token race conditions
  useEffect(() => {
    if (currentShop) {
      window.__currentShopData = currentShop;
    }
    return () => {
      delete window.__currentShopData;
    };
  }, [currentShop]);

  // Handle automatic token refresh for CCG tokens
  useEffect(() => {
    if (!currentShop || currentShop.tokenType !== 'ccg_custom_app' || !currentShop.clientId || !currentShop.clientSecret) {
      return;
    }

    const checkAndRefreshToken = async () => {
      // Check if token expires within the next 15 minutes
      const bufferMs = 15 * 60 * 1000; 
      if (!currentShop.tokenExpiresAt || Date.now() > (currentShop.tokenExpiresAt - bufferMs)) {
        try {
          console.log('Token nearing expiry, refreshing Shopify CCG token...');
          const res = await apiService.post('/api/shopify/ccg-token', {
            shop: currentShop.shop,
            clientId: currentShop.clientId,
            clientSecret: currentShop.clientSecret
          });
          
          const { access_token, expires_in } = res.data;
          
          const newExpiresAt = Date.now() + (expires_in * 1000) - (5 * 60 * 1000); // 5 min buffer inside the saved time

          const updatedShopData = {
            accessToken: access_token,
            tokenExpiresAt: newExpiresAt
          };

          // Save to Firebase directly via firebaseService to avoid circular context references
          await firebaseService.updateShop(currentShop.id, updatedShopData);
          
          // Also update the active shop preference so it doesn't hold a stale token
          if (user?.uid) {
            await saveLastSelectedShop(user.uid, { ...currentShop, ...updatedShopData });
          }
          
          // Update local state - use functional updates to avoid stale closures
          setCurrentShop(prev => {
            if (!prev || prev.id !== currentShop.id) return prev;
            return { ...prev, ...updatedShopData };
          });
          setShops(prev => prev.map(s => s.id === currentShop.id ? { ...s, ...updatedShopData } : s));
          
          console.log('CCG Token successfully refreshed in background.');
        } catch (error) {
          console.error('Failed to auto-refresh CCG token:', error);
        }
      }
    };

    // Check immediately upon active shop change
    checkAndRefreshToken();

    // Re-check every 10 minutes
    const intervalId = setInterval(checkAndRefreshToken, 10 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [currentShop]);

  async function loadShops() {
    try {
      setLoading(true);
      const shopList = await firebaseService.getShopsByUserId(user.uid);
      setShops(shopList);
      
      if (shopList.length > 0) {
        try {
          const lastActiveShop = await getLastSelectedShop(user.uid);
          if (lastActiveShop) {
            // NEVER use the full lastActiveShop object, as its token might be stale!
            // Only use its ID to grab the freshly loaded shop from the main collection.
            const freshShop = shopList.find(s => s.id === lastActiveShop.id);
            if (freshShop) {
              setCurrentShop(freshShop);
            } else {
              setCurrentShop(shopList[0]);
              await saveLastSelectedShop(user.uid, shopList[0]);
            }
          } else {
            // If no last active shop or it doesn't exist anymore, use first shop
            setCurrentShop(shopList[0]);
            await saveLastSelectedShop(user.uid, shopList[0]);
          }
        } catch (error) {
          console.error('Error loading last active shop:', error);
          setCurrentShop(shopList[0]); // Fallback to first shop
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading shops:', error);
      setLoading(false);
    }
  }

  // Function to set current shop and save to Firebase
  const handleSetCurrentShop = async (shop) => {
    if (!user) throw new Error('Must be logged in to set active shop');
    if (!shop) throw new Error('Shop is required');

    try {
      // Save to Firebase first
      await saveLastSelectedShop(user.uid, shop);
      // Then update local state
      setCurrentShop(shop);
    } catch (error) {
      console.error('Error setting current shop:', error);
      throw error;
    }
  };

  const value = {
    shops,
    setShops,
    currentShop,
    setCurrentShop: handleSetCurrentShop,
    loading,
    saveShop: async (shopData) => {
      const newShop = await firebaseService.saveShop(user.uid, shopData);
      setShops(prev => [...prev, newShop]);
      return newShop;
    },
    updateShop: async (shopId, shopData) => {
      const updatedShop = await firebaseService.updateShop(shopId, shopData);
      setShops(prev => prev.map(shop => 
        shop.id === shopId ? { ...shop, ...updatedShop } : shop
      ));
      // Update current shop if it's the one being edited
      if (currentShop?.id === shopId) {
        await handleSetCurrentShop({ ...currentShop, ...updatedShop });
      }
      return updatedShop;
    },
    deleteShop: async (shopId) => {
      if (!user) throw new Error('User must be logged in to delete a shop');
      await firebaseService.deleteShop(shopId, user.uid);
      setShops(prev => prev.filter(shop => shop.id !== shopId));
      // Clear current shop if it's the one being deleted
      if (currentShop?.id === shopId) {
        setCurrentShop(null);
      }
    },
    getShopMetafields: firebaseService.getShopMetafields,
    addShopMetafield: async (shopId, metafieldData) => {
      const newMetafield = await firebaseService.addShopMetafield(shopId, metafieldData);
      setShops(prev => prev.map(shop => {
        if (shop.id === shopId) {
          return {
            ...shop,
            customMetafields: [...(shop.customMetafields || []), newMetafield]
          };
        }
        return shop;
      }));
      return newMetafield;
    },
    removeShopMetafield: async (shopId, namespace, key) => {
      await firebaseService.removeShopMetafield(shopId, namespace, key);
      setShops(prev => prev.map(shop => {
        if (shop.id === shopId) {
          return {
            ...shop,
            customMetafields: (shop.customMetafields || []).filter(
              mf => !(mf.namespace === namespace && mf.key === key)
            )
          };
        }
        return shop;
      }));
    }
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
}
